/**
 * SecurityProvider
 * Centralized security provider that wraps all security-related contexts
 * Single point of reference for security and multi-tenancy concerns
 */

import React from 'react';
import { TenantProvider } from '@/contexts/TenantContext';
import { EnhancedAuthProvider } from '@/features/auth/contexts/EnhancedAuthContext';
import * as audit from './audit';

interface SecurityContextValue {
  audit: typeof audit;
}

const SecurityContext = React.createContext<SecurityContextValue | undefined>(undefined);

interface SecurityProviderProps {
  children: React.ReactNode;
}

/**
 * SecurityProvider - Wraps all security-related providers
 * Provides single point of access to all security features
 */
export const SecurityProvider: React.FC<SecurityProviderProps> = ({ children }) => {
  const securityValue: SecurityContextValue = {
    audit
  };

  return (
    <EnhancedAuthProvider>
      <TenantProvider>
        <SecurityContext.Provider value={securityValue}>{children}</SecurityContext.Provider>
      </TenantProvider>
    </EnhancedAuthProvider>
  );
};

/**
 * useSecurity hook - Single point of reference for all security hooks
 * Returns all security-related contexts and utilities
 *
 * Note: This hook should be used instead of useTenant/useAuth directly
 * for consistency and to access all security features in one place
 */
export const useSecurity = () => {
  const securityContext = React.useContext(SecurityContext);
  if (!securityContext) {
    throw new Error('useSecurity must be used within a SecurityProvider');
  }

  // Note: Individual hooks (useTenant, useAuth) should still be imported
  // directly by components when needed. This hook provides a convenience
  // wrapper for accessing all security features together.
  //
  // To use:
  // import { useSecurity } from '@/lib/security';
  // const { tenant, permissions, audit } = useSecurity();
  //
  // Or import hooks individually:
  // import { useTenant } from '@/contexts/TenantContext';
  // import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';

  return {
    audit: securityContext.audit
  };
};
